/**
 * Gestión de Plantillas — por tipo de documento (§3.3 CORRECCIÓN 3).
 *
 * Ruta: /admin/plantillas/tipo/:tipo
 * Muestra únicamente las plantillas del tipo seleccionado con acciones:
 *   Nueva · Editar · Vista previa · Activar/Desactivar · Establecer predeterminada
 *   · Duplicar · Volver.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import {
  ArrowLeftIcon, PlusIcon, PencilSimpleIcon, TrashIcon, CopyIcon,
  CheckCircleIcon, EyeIcon, StarIcon, PowerIcon, LockIcon,
} from "@phosphor-icons/react";
import PreviewRealDocModal from "./_templates/PreviewRealDocModal";
import DeactivateDefaultModal from "./_templates/DeactivateDefaultModal";

const DOC_TYPE_LABELS = {
  factura: "Facturas",
  proforma: "Proformas",
  orden_compra: "Órdenes de Compra",
  guia_remision: "Guías de Remisión",
  recibo: "Recibos de Pago",
  nota_credito: "Notas de Crédito",
  nota_debito: "Notas de Débito",
  estado_cuenta: "Estados de Cuenta",
};

const FORMATO_UNICO = ["factura", "proforma", "nota_credito", "nota_debito", "orden_compra", "estado_cuenta"];

export default function TemplatesByType() {
  const { tipo } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [deactTpl, setDeactTpl] = useState(null);

  const label = DOC_TYPE_LABELS[tipo] || tipo;
  // Factura, proforma, NC, ND, orden de compra y estado de cuenta: único formato aprobado
  const formatoUnico = FORMATO_UNICO.includes(tipo);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/document-templates", { params: { tipo } });
      setItems(data);
    } catch (e) {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, [tipo]);

  useEffect(() => {
    load();
    api.get("/document-templates/summary-by-type").then(({ data }) => setCanEdit(Boolean(data.puede_editar)))
      .catch(() => {});
  }, [load]);

  const setAsDefault = async (t) => {
    try {
      await api.post(`/document-templates/${t.id}/set-default`);
      toast.success(`"${t.nombre}" establecida como predeterminada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggleActive = async (t, nuevaPredeterminada) => {
    // §3.5 – bloquear solo si esta es la ÚLTIMA activa (backend valida también con 409)
    const otherActive = items.filter((x) => x.id !== t.id && x.activo).length;
    if (t.activo && otherActive === 0) {
      toast.error(
        "No puedes desactivar la única plantilla activa del tipo. Crea o activa otra primero."
      );
      return;
    }
    // Desactivar la predeterminada exige elegir otra (modal)
    if (t.activo && t.es_predeterminada && nuevaPredeterminada === undefined) {
      setDeactTpl(t);
      return;
    }
    try {
      await api.post(`/document-templates/${t.id}/toggle-active`, null,
        { params: nuevaPredeterminada ? { nueva_predeterminada: nuevaPredeterminada } : {} });
      toast.success(t.activo ? "Desactivada" : "Activada");
      setDeactTpl(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const duplicate = async (t) => {
    try {
      const { data } = await api.post(`/document-templates/${t.id}/duplicate`);
      toast.success("Duplicada");
      navigate(`/admin/plantillas/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar "${t.nombre}" (v${t.version})? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/document-templates/${t.id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch (e) {
      // Las plantillas usadas en documentos emitidos no se eliminan: se desactivan.
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const preview = (t) => setPreviewTpl(t);

  return (
    <Layout>
      <div className="p-6" data-testid={`templates-tipo-${tipo}`}>
        <button
          onClick={() => navigate("/admin/plantillas")}
          data-testid="btn-back-templates"
          className="text-agro-green hover:underline text-xs flex items-center gap-1.5 mb-4"
        >
          <ArrowLeftIcon size={12} /> Volver a Gestión de Plantillas
        </button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Plantillas de</div>
            <h1 className="text-2xl font-heading font-bold text-text-primary">{label}</h1>
            <p className="text-xs text-text-muted mt-1">
              Solo se muestran plantillas de este tipo. La marcada como predeterminada (con estrella) se usará
              automáticamente al emitir el documento.
            </p>
          </div>
          {canEdit && !formatoUnico && <button
            onClick={() => navigate(`/admin/plantillas/new?tipo=${tipo}`)}
            data-testid="btn-new-template"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md px-4 py-2 flex items-center gap-2 text-sm"
          >
            <PlusIcon size={16} weight="bold" /> Nueva plantilla
          </button>}
        </div>

        {formatoUnico && (
          <div className="mb-4 bg-agro-green/5 border border-agro-green/30 rounded-md p-3 text-xs text-text-secondary" data-testid="formato-unico-banner">
            <b className="text-agro-green">Formato único aprobado.</b> {label} se visualizan, imprimen y exportan
            siempre con el “Diseño institucional AGROPORT”. Sus datos institucionales y fiscales se administran
            en Configuración de documentos y datos fiscales.
          </div>
        )}
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Nombre</th>
                <th className="p-3">Versión</th>
                <th className="p-3">Estado</th>
                <th className="p-3">Predeterminada</th>
                <th className="p-3">Alcance</th>
                <th className="p-3">Usos</th>
                <th className="p-3">Última modificación</th>
                <th className="p-3">Modificado por</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="9" className="p-12 text-center text-text-muted">
                  Aún no hay plantillas de {label.toLowerCase()}. Crea la primera con el botón &quot;Nueva plantilla&quot;.
                </td></tr>
              ) : items.map((t) => (
                <tr key={t.id} className={`border-t border-border/40 hover:bg-white/[0.02] ${!t.activo ? "opacity-60" : ""}`}
                  data-testid={`tpl-row-${t.id}`}>
                  <td className="p-3 pl-5 font-medium flex items-center gap-2">
                    {t.es_predeterminada && (
                      <StarIcon size={14} weight="fill" className="text-agro-yellow" title="Predeterminada" />
                    )}
                    <span>{t.nombre}</span>
                    {t.es_formato_sistema && (
                      <span className="text-[9px] uppercase tracking-wider rounded px-1.5 py-0.5 bg-agro-green/10 text-agro-green"
                        title="Formato oficial actual del sistema. Al editarlo se publica una nueva versión.">
                        Sistema
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-agro-green font-mono">v{t.version}</td>
                  <td className="p-3">
                    {t.activo ? (
                      <span className="inline-flex items-center gap-1 text-agro-green text-xs">
                        <CheckCircleIcon size={12} weight="fill" /> Activa
                      </span>
                    ) : (
                      <span className="text-text-muted text-xs">Inactiva</span>
                    )}
                  </td>
                  <td className="p-3">
                    {t.es_predeterminada ? (
                      <span className="text-agro-yellow text-xs inline-flex items-center gap-1">
                        <StarIcon size={12} weight="fill" /> Sí
                      </span>
                    ) : t.activo && canEdit ? (
                      <button
                        onClick={() => setAsDefault(t)}
                        data-testid={`btn-set-default-${t.id}`}
                        className="text-xs text-text-muted hover:text-agro-yellow underline"
                      >
                        Establecer
                      </button>
                    ) : (
                      <span className="text-text-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-text-muted" data-testid={`tpl-alcance-${t.id}`}>
                    {t.alcance === "sucursal" ? "Sucursal" : "Empresa"}
                  </td>
                  <td className="p-3 text-xs text-text-muted">
                    {t.usos > 0 ? (
                      <span className="inline-flex items-center gap-1" title="Usada en documentos emitidos: no se puede eliminar">
                        <LockIcon size={11} /> {t.usos}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-xs text-text-muted">
                    {(t.updated_at || t.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-3 text-xs text-text-muted">
                    {t.modified_by || t.created_by || "—"}
                  </td>
                  <td className="p-3 pr-5 text-right whitespace-nowrap">
                    <button onClick={() => preview(t)}
                      className="p-1.5 text-text-muted hover:text-agro-yellow" title="Vista previa" aria-label="Vista previa">
                      <EyeIcon size={16} />
                    </button>
                    {canEdit && !t.formato_unico && (<>
                    <button onClick={() => navigate(`/admin/plantillas/${t.id}`)}
                      className="p-1.5 text-text-muted hover:text-agro-green" title="Editar"
                      data-testid={`btn-edit-${t.id}`}>
                      <PencilSimpleIcon size={16} />
                    </button>
                    <button onClick={() => toggleActive(t)}
                      className={`p-1.5 ${t.activo ? "text-agro-green hover:text-red-400" : "text-text-muted hover:text-agro-green"}`}
                      title={t.activo ? "Desactivar" : "Activar"}
                      data-testid={`btn-toggle-${t.id}`}>
                      <PowerIcon size={16} />
                    </button>
                    <button onClick={() => duplicate(t)}
                      className="p-1.5 text-text-muted hover:text-agro-green" title="Duplicar"
                      data-testid={`btn-duplicate-${t.id}`}>
                      <CopyIcon size={16} />
                    </button>
                    {!(t.usos > 0) && !t.sistema_key && (
                      <button onClick={() => remove(t)}
                        className="p-1.5 text-text-muted hover:text-red-400" title="Eliminar (solo si nunca se usó)"
                        data-testid={`btn-delete-${t.id}`}>
                        <TrashIcon size={16} />
                      </button>
                    )}
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {previewTpl && <PreviewRealDocModal template={previewTpl} onClose={() => setPreviewTpl(null)} />}
        {deactTpl && (
          <DeactivateDefaultModal template={deactTpl}
            candidates={items.filter((x) => x.id !== deactTpl.id && x.activo)}
            onConfirm={(nid) => toggleActive(deactTpl, nid)} onClose={() => setDeactTpl(null)} />
        )}
      </div>
    </Layout>
  );
}
